import { useEffect, useState, useMemo, useRef } from "react";
import Markdown from "markdown-to-jsx";

interface BlockInTypeProps {
  fullText: string;
  minSpeed?: number;
  maxSpeed?: number;
  className?: string;
  onComplete?: () => void;
  onTextChange?: () => void;
  onProgress?: (progress: number) => void; // Callback para progresso da animação
  skipAnimation?: boolean; // Pula a animação de digitação
}

const BlockInType = ({
  fullText,
  minSpeed = 5,
  maxSpeed = 25,
  onComplete,
  onTextChange,
  className,
  onProgress,
  skipAnimation = false,
}: BlockInTypeProps) => {
  const [visibleText, setVisibleText] = useState("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAnimatingRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onTextChangeRef = useRef(onTextChange);
  const onProgressRef = useRef(onProgress);
  const currentFullTextRef = useRef(fullText);

  // Atualizar refs quando callbacks mudarem
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onTextChangeRef.current = onTextChange;
    onProgressRef.current = onProgress;
  }, [onComplete, onTextChange, onProgress]);

  // Quebra o texto em tokens
  const tokens = useMemo(() => {
    const tokenArray = fullText
      .split(/(\s+|\n+|[^\w\s]+)/g)
      .filter((token) => token.length > 0);

    return tokenArray;
  }, [fullText]);

  // Efeito apenas para mudanças de texto
  useEffect(() => {
    // Verifica se o texto realmente mudou
    const textChanged = currentFullTextRef.current !== fullText;

    // Se o texto não mudou E já está animando, não faz nada
    if (!textChanged && isAnimatingRef.current) {
      return;
    }

    // Atualiza a ref com o novo texto
    currentFullTextRef.current = fullText;

    // Limpar animação anterior apenas se o texto mudou
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    isAnimatingRef.current = false;

    // Se não há tokens, não faz nada
    if (tokens.length === 0) {
      setVisibleText("");
      return;
    }

    // Se deve pular animação, mostra tudo
    if (skipAnimation) {
      setVisibleText(fullText);
      onCompleteRef.current?.();
      return;
    }

    // Inicia animação
    isAnimatingRef.current = true;
    let currentIndex = 0;

    // Mostra o primeiro token imediatamente para evitar piscar
    setVisibleText(tokens[0]);
    currentIndex = 1;

    const typeNextToken = () => {
      if (!isAnimatingRef.current) {
        return;
      }

      if (currentIndex >= tokens.length) {
        isAnimatingRef.current = false;
        onCompleteRef.current?.();
        return;
      }

      const newText = tokens.slice(0, currentIndex + 1).join("");
      setVisibleText(newText);
      onTextChangeRef.current?.();
      currentIndex++;

      const speed =
        Math.floor(Math.random() * (maxSpeed - minSpeed + 1)) + minSpeed;
      timeoutRef.current = setTimeout(typeNextToken, speed);
    };

    // Inicia a animação do segundo token em diante
    if (tokens.length > 1) {
      setTimeout(typeNextToken, minSpeed);
    } else {
      // Se só tem um token, já terminou
      onCompleteRef.current?.();
    }

    return () => {
      // Só limpa se o texto mudou
      if (currentFullTextRef.current !== fullText) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        isAnimatingRef.current = false;
      }
    };
  }, [fullText, skipAnimation, tokens, minSpeed, maxSpeed]);

  // Progresso
  useEffect(() => {
    if (tokens.length === 0) return;
    const currentTokens = visibleText
      .split(/(\s+|\n+|[^\w\s]+)/g)
      .filter((t) => t.length > 0);
    const progress = Math.min(currentTokens.length / tokens.length, 1);
    onProgressRef.current?.(progress);
  }, [visibleText, tokens.length]);

  return (
    <div className={className} style={{ minHeight: '1em', willChange: 'contents' }}>
      <div className="paragraph">
        <Markdown options={{ forceBlock: true }}>{visibleText}</Markdown>
      </div>
    </div>
  );
};

export default BlockInType;
